import React, { useState, useEffect, useRef } from "react";
import { Edit3, Check, X, Loader2 } from "lucide-react";
import * as api from "../../api";

/* -----------------------------------------------------------------------------
 * InlineCostEdit — casi imperceptible cost override on each admin order card.
 *
 * Renders as a subtle "💰 $Y · Gan. $Z ✏️" line. Clicking the pencil turns
 * `$Y` into a compact numeric input. Enter or blur saves via
 * `PATCH /api/orders/{id}/cost`; Esc cancels. Profit updates in real-time
 * as the admin types.
 * --------------------------------------------------------------------------- */

const totalCost = (order) => {
  const items = order?.items || [];
  if (items.length > 0) {
    return items.reduce((s, it) => s + Number(it.costPriceUsd || 0), 0);
  }
  return Number(order?.costPriceUsd || 0);
};

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export function InlineCostEdit({ order, onUpdated, showToast }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(totalCost(order).toFixed(2));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setValue(totalCost(order).toFixed(2));
  }, [order, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const sale = Number(order?.priceUsd || 0);
  const currentCost = editing ? Number(String(value).replace(",", ".")) : totalCost(order);
  const profit = sale - (isNaN(currentCost) ? 0 : currentCost);

  const commit = async () => {
    const parsed = Number(String(value).replace(",", "."));
    if (isNaN(parsed) || parsed < 0) {
      showToast?.("Costo inválido", "Ingresa un número >= 0.", "error");
      return;
    }
    if (Math.abs(parsed - totalCost(order)) < 0.001) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await api.updateOrderCost(order.id, parsed);
      onUpdated?.(updated);
      showToast?.("Costo Actualizado", `Nuevo costo: ${fmt(parsed)} · Ganancia: ${fmt(sale - parsed)}`, "success");
      setEditing(false);
    } catch (err) {
      showToast?.("Error", err?.message || "No se pudo guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setValue(totalCost(order).toFixed(2));
    setEditing(false);
  };

  return (
    <div
      className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 mt-0.5"
      data-testid={`inline-cost-edit-${order.id}`}
    >
      <span>Costo:</span>
      {editing ? (
        <>
          <span className="text-zinc-600">$</span>
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              else if (e.key === "Escape") cancel();
            }}
            onBlur={commit}
            disabled={saving}
            data-testid={`cost-input-${order.id}`}
            className="w-14 bg-ag7-surface-2 border border-ag7-purple/50 rounded px-1 py-0.5 text-[10px] text-white outline-none focus:border-purple-400"
          />
          {saving ? (
            <Loader2 className="w-3 h-3 animate-spin text-purple-300" />
          ) : (
            <>
              <button
                onMouseDown={(e) => { e.preventDefault(); commit(); }}
                data-testid={`cost-save-${order.id}`}
                className="text-emerald-400 hover:text-emerald-300"
                title="Guardar"
              >
                <Check className="w-3 h-3" />
              </button>
              <button
                onMouseDown={(e) => { e.preventDefault(); cancel(); }}
                data-testid={`cost-cancel-${order.id}`}
                className="text-zinc-500 hover:text-white"
                title="Cancelar"
              >
                <X className="w-3 h-3" />
              </button>
            </>
          )}
        </>
      ) : (
        <>
          <span className="text-amber-300/90">{fmt(totalCost(order))}</span>
          <button
            onClick={() => setEditing(true)}
            data-testid={`cost-edit-btn-${order.id}`}
            className="text-zinc-600 hover:text-purple-300 transition-colors"
            title="Editar costo del proveedor"
          >
            <Edit3 className="w-3 h-3" />
          </button>
        </>
      )}
      <span className="text-zinc-700">·</span>
      <span>Gan.</span>
      <span className={profit >= 0 ? "text-emerald-300/90" : "text-red-300/90"}>
        {fmt(profit)}
      </span>
    </div>
  );
}
